/**
 * Shared localStorage-backed saved search store.
 *
 * The Search page and the Track Translations page both let a user name and
 * re-apply a filter selection, and both showed it the same way. The read,
 * validate, write, evict and change-notification logic was identical between
 * them — only the storage key, the shape of the saved state, and the label
 * wording differed. Those three things are config; everything else lives here.
 *
 * TEMPORARY STORAGE — localStorage is a stopgap, not the intended design.
 *
 * Consequences a reader should be aware of:
 * - Saved searches are per-browser and per-device: they do not follow the user
 *   to another machine, another browser, or an incognito window.
 * - They are lost whenever site data is cleared.
 * - There is no server-side record, so nothing can be shared or audited.
 *
 * Long term these move into the users DynamoDB table via the user DAO, so they
 * are keyed to the authenticated user and follow them everywhere. That work is
 * deliberately out of scope here and lands in a follow-up CR. When it does,
 * this module becomes the local cache layer (or is deleted outright).
 */

/** Default number of saved searches kept per store before the oldest is evicted. */
export const DEFAULT_MAX_SAVED_SEARCHES = 5;

/** A single saved search entry, generic over the state each page persists. */
export interface SavedSearchEntry<TState> {
  id: string;
  label: string;
  state: TState;
  savedAt: number;
}

/**
 * Brand marking a `parseState` return value as a REPAIRED state rather than a
 * plain one. A symbol key can never collide with a field of the caller's
 * `TState`, so the store can discriminate the two shapes safely.
 */
const REPAIRED = Symbol('savedSearchStore.repaired');

/** A state that parsed only after a recoverable defect was corrected. */
export interface RepairedState<TState> {
  readonly [REPAIRED]: true;
  readonly state: TState;
}

/**
 * Wraps a state a validator had to REPAIR to make usable.
 *
 * `parseState` has three outcomes, not two:
 * - a plain `TState` — the stored entry was already valid;
 * - `repaired(state)` — the entry had a defect the validator could correct
 *   without losing what the user saved, so it is KEPT in corrected form and
 *   storage is rewritten canonically;
 * - `null` — unrecoverable, so the entry is dropped.
 *
 * Distinguishing the middle case is what lets a validator repair instead of
 * drop: returning a plain `TState` would keep the entry but leave storage
 * holding the defective copy, and returning `null` would delete a saved search
 * the user can still use.
 */
export function repaired<TState>(state: TState): RepairedState<TState> {
  return { [REPAIRED]: true, state };
}

function isRepaired<TState>(
  value: TState | RepairedState<TState>,
): value is RepairedState<TState> {
  return (
    typeof value === 'object' &&
    value !== null &&
    (value as RepairedState<TState>)[REPAIRED] === true
  );
}

export interface SavedSearchStoreConfig<TState> {
  /** localStorage key. Must be unique per store. */
  readonly storageKey: string;
  /**
   * Window event dispatched after every write, so bars in the *same* tab
   * refresh. The browser's native `storage` event only fires in other tabs.
   * Must be unique per store, or one page's writes will churn the other's bar.
   */
  readonly changedEvent: string;
  /** Entries kept before the oldest is evicted. Defaults to 5. */
  readonly maxEntries?: number;
  /**
   * Validates one raw stored state, returning null when it is unusable so the
   * entry can be dropped, or `repaired(state)` when a recoverable defect was
   * corrected. localStorage can hold data written by an older build, so this is
   * the boundary where that is caught.
   */
  readonly parseState: (raw: unknown) => TState | RepairedState<TState> | null;
  /** Suggested name, used when the caller supplies none. */
  readonly generateLabel: (state: TState) => string;
}

export interface SavedSearchStore<TState> {
  /** Event dispatched after each write. Subscribe to refresh in the same tab. */
  readonly changedEvent: string;
  /** Entries kept before the oldest is evicted. */
  readonly maxEntries: number;
  getAll(): SavedSearchEntry<TState>[];
  save(state: TState, name?: string): void;
  remove(id: string): void;
}

export function createSavedSearchStore<TState>(
  config: SavedSearchStoreConfig<TState>,
): SavedSearchStore<TState> {
  const {
    storageKey,
    changedEvent,
    maxEntries = DEFAULT_MAX_SAVED_SEARCHES,
    parseState,
    generateLabel,
  } = config;

  /**
   * Reads saved searches, dropping any entry that no longer parses and keeping
   * — in corrected form — any entry the validator could repair. If anything was
   * dropped or repaired the cleaned list is written back, so the same work is
   * not repeated on every read.
   */
  function getAll(): SavedSearchEntry<TState>[] {
    try {
      const raw = localStorage.getItem(storageKey);
      if (!raw) return [];

      const parsed = JSON.parse(raw) as Array<Record<string, unknown>>;
      if (!Array.isArray(parsed)) return [];

      const valid: SavedSearchEntry<TState>[] = [];
      let needsRewrite = false;

      for (const entry of parsed) {
        if (
          !entry ||
          typeof entry !== 'object' ||
          typeof entry.id !== 'string' ||
          typeof entry.label !== 'string'
        ) {
          needsRewrite = true;
          continue;
        }

        const result = parseState(entry.state);
        if (result === null) {
          needsRewrite = true;
          continue;
        }

        // A repaired entry survives, but storage is left canonical.
        let state: TState;
        if (isRepaired(result)) {
          state = result.state;
          needsRewrite = true;
        } else {
          state = result;
        }

        valid.push({
          id: entry.id,
          label: entry.label,
          state,
          savedAt: typeof entry.savedAt === 'number' ? entry.savedAt : 0,
        });
      }

      if (needsRewrite) {
        localStorage.setItem(storageKey, JSON.stringify(valid));
      }

      return valid;
    } catch {
      // Unparseable or unavailable storage (private mode, quota) — behave as
      // empty rather than breaking the page.
      return [];
    }
  }

  /** Saves a search under the given name, falling back to a generated label. */
  function save(state: TState, name?: string): void {
    const searches = getAll();
    const trimmed = name?.trim();
    const label =
      trimmed && trimmed.length > 0 ? trimmed : generateLabel(state);

    searches.push({
      id: crypto.randomUUID(),
      label,
      state,
      savedAt: Date.now(),
    });

    // Oldest entries sit at the front, so evicting from the front drops the
    // least recently saved.
    while (searches.length > maxEntries) {
      searches.shift();
    }

    write(searches);
  }

  function remove(id: string): void {
    write(getAll().filter((search) => search.id !== id));
  }

  function write(searches: SavedSearchEntry<TState>[]): void {
    localStorage.setItem(storageKey, JSON.stringify(searches));
    window.dispatchEvent(new Event(changedEvent));
  }

  return { changedEvent, maxEntries, getAll, save, remove };
}
